import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useAuthContext } from '../contexts/AuthContext';
import { useDismissiblePanel } from '../contexts/UIPreferencesContext';
import { useFlag } from '../hooks/useFeatureFlags';
import { useProfile } from '../hooks/useProfile';
import { billingApi, endpointsApi } from '../services/api';
import { useAccountPlan, useAccountUsage } from '../hooks/useAccountUsage';
import ServicesTable, {
  type ServicesTableUsageCost,
} from '../components/services/ServicesTable';
import DashboardOnboarding, {
  DASHBOARD_ONBOARDING_PANEL_KEY,
} from '../components/dashboard/DashboardOnboarding';
import { EmptyState } from '../components/shared/EmptyState';
import SpendingBar from '../components/ui/SpendingBar';
import { SPENDING_TEXT_CLASSES, formatBilledCents, formatSpendingCents, spendingReading } from '../utils/spending';

/**
 * Dashboard landing page (task 378, Mockup J).
 *
 * The post-auth front door: three KPI tiles and the shared services table —
 * nothing else. Every value is traceable to a live API:
 *   - GET /accounts/usage      → deployedEndpoints · endpointCallCount · rawCostMicroUsd · period
 *                                · byEndpoint (the per-service cost column, task 1010)
 *   - GET /endpoints           → table rows + pagination.total ("of N defined")
 *   - GET /billing/plans       → the AMS catalog entry for the JWT plan slug:
 *                                limits.included_endpoint_calls and
 *                                limits.model_cost_markup_bps
 *
 * Plan limits are read LIVE from the AMS catalog (billing-plans proxy), never
 * from the JWT `limits` claim (stale until a forced token refresh). The JWT
 * contributes only the plan slug. When the plan or a limit is missing or -1,
 * the corresponding line is omitted rather than showing a wrong number.
 */

/** How many services to list on the dashboard table. */
const DASHBOARD_SERVICES_LIMIT = 10;

const EM_DASH = '—';

/** "2026-08" → "Aug"; null when the period is absent or unparseable. */
function periodMonthLabel(period: string | undefined): string | null {
  if (!period) return null;
  const match = /^(\d{4})-(\d{2})$/.exec(period);
  if (!match) return null;
  const monthIndex = Number(match[2]) - 1;
  if (monthIndex < 0 || monthIndex > 11) return null;
  return new Date(Number(match[1]), monthIndex, 1).toLocaleString('en-US', { month: 'short' });
}

const usdFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function timeOfDayGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/** A catalog limit is usable only when present and not the -1 "unlimited" sentinel. */
function usableLimit(value: number | undefined): number | undefined {
  return typeof value === 'number' && value >= 0 ? value : undefined;
}

function KpiTile({
  label,
  value,
  detail,
  testId,
  footer,
}: {
  label: string;
  value: string;
  detail?: React.ReactNode;
  testId: string;
  /** Rendered under the detail line — the cost tile's spending bar (task 1159). */
  footer?: React.ReactNode;
}) {
  return (
    <div
      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-5"
      data-testid={testId}
    >
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1.5 text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
        {value}
      </div>
      {detail && <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{detail}</div>}
      {footer && <div className="mt-2">{footer}</div>}
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthContext();

  // --- Who the platform says this User is (task 596) --------------------------
  // AMS-owned profile, read through the innovate-service proxy. Feeds the
  // greeting here and the onboarding welcome below from ONE fetch.
  const { data: profile } = useProfile();

  // --- First-run onboarding state (task 525, Mockup E) ------------------------
  // Two states behind one nav item: the checklist replaces the stats layout
  // until the account has a live AI Service, and flips permanently once it
  // does. The User can also dismiss it early (persisted preference).
  const onboardingEnabled = useFlag('innovate.fe.dashboard.onboarding');
  const onboarding = useDismissiblePanel(DASHBOARD_ONBOARDING_PANEL_KEY);

  // --- Current-period usage summary (GET /accounts/usage) --------------------
  // The shared query — it also supplies the plan slug below.
  const { data: usage, isLoading: usageLoading } = useAccountUsage();
  // The spending limit, when one is set (task 1159): the cost tile adds
  // "$X of $Y limit" in the reading's colour and the mini bar.
  const spending = spendingReading(usage?.spending);
  const { plan, isResolved: planResolved } = useAccountPlan();

  // --- Services list (GET /endpoints) — table rows + "of N defined" ----------
  const { data: endpointsData, isLoading: endpointsLoading } = useQuery({
    queryKey: ['endpoints', 1, DASHBOARD_SERVICES_LIMIT, undefined],
    queryFn: () => endpointsApi.getAll(1, DASHBOARD_SERVICES_LIMIT).then((res) => res.data),
  });
  const endpoints = endpointsData?.data ?? [];
  const definedTotal = endpointsData?.pagination?.total;

  // --- Live AMS plan catalog (GET /billing/plans) -----------------------------
  // The usage endpoint contributes ONLY the plan slug (task 676 — previously
  // the JWT); the limit values come from the catalog.
  const { data: plans } = useQuery({
    queryKey: ['planCatalog'],
    queryFn: () => billingApi.getPlans().then((res) => res.data.data.plans),
    staleTime: 10 * 60_000,
    retry: 1,
  });
  const currentPlan = plan ? plans?.find((entry) => entry.slug === plan) : undefined;
  const includedCalls = usableLimit(currentPlan?.limits['included_endpoint_calls']);
  const markupBps = usableLimit(currentPlan?.limits['model_cost_markup_bps']);
  const markupMultiplier = markupBps !== undefined ? markupBps / 10_000 : undefined;

  // --- Tile values ------------------------------------------------------------
  const monthLabel = periodMonthLabel(usage?.period);
  const billedUsageDollars =
    usage?.rawCostMicroUsd !== undefined && markupMultiplier !== undefined
      ? (usage.rawCostMicroUsd / 1_000_000) * markupMultiplier
      : undefined;

  // The greeting name is the platform `displayName` read through the profile
  // proxy (task 596) — AMS resolves it (an explicitly chosen name, else
  // first + last, else the email local part), so this surface never guesses.
  // Token claims are not consulted: a User who registered without a name has
  // no `given_name`, and a name added on the Profile page would not appear in
  // the token until it was refreshed.
  const greetingName = profile?.displayName ?? '';
  const greeting = `${timeOfDayGreeting()}${greetingName ? `, ${greetingName}` : ''}`;

  // Billing affordances are gated by innovate.fe.billing.page, whose
  // role-owner cohort is the owner check (task 692). The role claim is no
  // longer read here: evaluation is uniform and, as a permission-type flag,
  // fails closed during an Unleash outage.
  const canManageBilling = useFlag('innovate.fe.billing.page');

  // --- Per-service cost column (task 1010) ------------------------------------
  // A rollback lever, baked on. Read unconditionally — hooks may not be
  // conditional — and consulted only when passing the prop below.
  const showServiceCost = useFlag('innovate.fe.dashboard.service-cost');
  // The New AI Service entries follow /services/new's own flag (task 1240).
  const newServiceEnabled = useFlag('innovate.fe.services.new.page');

  // Every figure below comes from the SAME usage response as the tile above and
  // carries the same markup, so the footer Total is the tile by construction.
  // `byEndpoint` absent (an older innovate-service build) is "cannot compute",
  // which the undefined total turns into em dashes rather than a false $0.00.
  const byEndpointDollars: Record<string, number> = {};
  if (usage?.byEndpoint && markupMultiplier !== undefined) {
    for (const row of usage.byEndpoint) {
      byEndpointDollars[row.endpointId] =
        (row.costMicroUsd / 1_000_000) * markupMultiplier;
    }
  }
  const costColumnTotal =
    usage?.byEndpoint !== undefined ? billedUsageDollars : undefined;
  const listedCostSum = endpoints.reduce(
    (sum, endpoint) => sum + (byEndpointDollars[endpoint.id] ?? 0),
    0,
  );
  // Archived services keep their usage rows and the table lists at most ten, so
  // the listed rows may legitimately fall short of the account total. Clamp at
  // zero and omit the line when there is nothing left over.
  const remainder =
    costColumnTotal !== undefined ? Math.max(costColumnTotal - listedCostSum, 0) : 0;
  const serviceCostProp: ServicesTableUsageCost = {
    byEndpointDollars,
    totalDollars: costColumnTotal,
    remainderDollars: remainder > 0.005 ? remainder : undefined,
    periodLabel: monthLabel,
  };

  // A "live AI Service" is what the usage summary already calls deployed; the
  // defined total is the fallback while /accounts/usage is unavailable. Both
  // queries must have settled first, so the checklist never flashes over stats.
  const liveServiceCount = usage?.deployedEndpoints ?? definedTotal ?? 0;
  const showOnboarding =
    onboardingEnabled &&
    !onboarding.isDismissed &&
    !usageLoading &&
    !endpointsLoading &&
    liveServiceCount === 0;

  const servicesEmptyState = (
    <EmptyState
      title="No AI Services yet"
      description="Create your first AI Service — describe what you want and Innovate assembles a working API you can call from anywhere."
      action={newServiceEnabled ? { label: 'New AI Service', onClick: () => navigate('/services/new') } : undefined}
    />
  );

  if (showOnboarding) {
    return (
      <div data-testid="dashboard-page">
        <DashboardOnboarding
          servicesTotal={definedTotal ?? 0}
          onDismiss={onboarding.dismiss}
        />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      {/* Header row: greeting + New AI Service */}
      <div className="sm:flex sm:items-start sm:justify-between gap-4">
        <div>
          <h1
            className="text-2xl font-bold text-gray-900 dark:text-white"
            data-testid="dashboard-greeting"
          >
            {greeting}
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Here's how your AI Services are doing. Building and refining them happens in{' '}
            <span className="font-semibold">AI Services</span>.
          </p>
        </div>
        {newServiceEnabled && (
        <div className="mt-3 sm:mt-0">
          <button
            onClick={() => navigate('/services/new')}
            data-testid="dashboard-new-service"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-700 hover:bg-indigo-700 dark:hover:bg-indigo-800"
          >
            <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
            New AI Service
          </button>
        </div>
        )}
      </div>

      {/* No-plan empty state (task 466): with no plan there are no limits to
          show and every AI action fails server-side, so silent em dashes are
          replaced by an explicit, role-aware explanation + CTA. */}
      {user && planResolved && !plan ? (
        <div
          data-testid="dashboard-no-plan"
          className="mt-6 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-6 text-center"
        >
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            Your account doesn't have a plan yet
          </p>
          {canManageBilling ? (
            <>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                AI features are disabled until you choose one. Usage and plan
                limits will appear here once a plan is active.
              </p>
              <Link
                to="/settings/subscription"
                data-testid="dashboard-no-plan-cta"
                className="mt-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-700 hover:bg-indigo-700 dark:hover:bg-indigo-800"
              >
                Choose a plan
              </Link>
            </>
          ) : (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Ask your account owner to choose a plan. Usage and plan limits
              will appear here once a plan is active.
            </p>
          )}
        </div>
      ) : (
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <KpiTile
          testId="dashboard-tile-deployed"
          label="Deployed AI Services"
          value={usage?.deployedEndpoints !== undefined ? String(usage.deployedEndpoints) : EM_DASH}
          detail={definedTotal !== undefined ? `of ${definedTotal} defined` : null}
        />
        <KpiTile
          testId="dashboard-tile-calls"
          label={monthLabel ? `Calls this period · ${monthLabel}` : 'Calls this period'}
          value={
            usage?.endpointCallCount !== undefined
              ? usage.endpointCallCount.toLocaleString()
              : EM_DASH
          }
          detail={
            includedCalls !== undefined
              ? `${includedCalls.toLocaleString()} included in your plan`
              : null
          }
        />
        <KpiTile
          testId="dashboard-tile-cost"
          label="Usage cost this period"
          value={billedUsageDollars !== undefined ? usdFormatter.format(billedUsageDollars) : EM_DASH}
          detail={
            spending.hasLimit ? (
              <span data-testid="dashboard-tile-cost-limit">
                <span className={`font-medium ${SPENDING_TEXT_CLASSES[spending.tone]}`}>
                  {spending.stopped
                    ? 'limit reached · external calls stopped'
                    : `${formatBilledCents(spending.billedCents)} of ${formatSpendingCents(spending.limitCents as number)} limit`}
                </span>
                {billedUsageDollars !== undefined && !spending.stopped ? ' · LLM cost incl. plan markup' : ''}
              </span>
            ) : billedUsageDollars !== undefined ? (
              'LLM cost incl. plan markup'
            ) : null
          }
          footer={spending.hasLimit ? <SpendingBar reading={spending} compact testId="dashboard-spending-bar" /> : null}
        />
      </div>
      )}

      {/* Services table */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Your AI Services</h2>
          <Link
            to="/services"
            data-testid="dashboard-services-link"
            className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
          >
            Open AI Services →
          </Link>
        </div>
        <ServicesTable
          endpoints={endpoints}
          isLoading={endpointsLoading}
          emptyState={servicesEmptyState}
          usageCost={showServiceCost ? serviceCostProp : undefined}
        />
      </div>
    </div>
  );
}
